import { getSessionUUID } from '../components/lib/AuthToken';
import logger from '../utils/logger';

const BASE = '/integrations';

async function authHeaders() {
    const token = await getSessionUUID();
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
    };
}

async function apiFetch(path, options = {}) {
    try {
        const headers = await authHeaders();
        if (options.body instanceof FormData) delete headers['Content-Type'];
        const resp = await fetch(`${BASE}${path}`, { ...options, headers: { ...headers, ...(options.headers || {}) } });
        const body = await resp.json().catch(() => ({}));
        return { ok: resp.ok, status: resp.status, data: body };
    } catch (err) {
        logger.error('[integrations api]', err);
        return { ok: false, status: 500, data: { error: err.message } };
    }
}

// ── Providers ─────────────────────────────────────────────────────────────────
export const getProviders = () => apiFetch('/providers');

// ── Integrations CRUD ─────────────────────────────────────────────────────────
export const listIntegrations    = ()           => apiFetch('/');
export const getIntegration      = (id)         => apiFetch(`/${id}`);
export const createIntegration   = (data)       => apiFetch('/', { method: 'POST', body: JSON.stringify(data) });
export const updateIntegration   = (id, data)   => apiFetch(`/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteIntegration   = (id)         => apiFetch(`/${id}`, { method: 'DELETE' });

// ── Endpoints ─────────────────────────────────────────────────────────────────
export const listEndpoints  = (id)       => apiFetch(`/${id}/endpoints`);
export const upsertEndpoint = (id, data) => apiFetch(`/${id}/endpoints`, { method: 'POST', body: JSON.stringify(data) });
export const deleteEndpoint = (epId)     => apiFetch(`/endpoints/${epId}`, { method: 'DELETE' });

// ── Test + Sync ───────────────────────────────────────────────────────────────
export const testConnection = (id)           => apiFetch(`/${id}/test`,     { method: 'POST' });
export const runSync        = (id)           => apiFetch(`/${id}/sync`,     { method: 'POST' });
export const runManifest    = (id)           => apiFetch(`/${id}/manifest`, { method: 'POST' });
export const printPallettrackConfiguration = (id) =>
    apiFetch(`/${id}/pallettrack-configuration/print`, { method: 'POST' });
export const getSyncLog     = (id, limit=50) => apiFetch(`/${id}/sync-log?limit=${limit}`);

// ── Vehicle map ───────────────────────────────────────────────────────────────
export const listVehicleMap  = (id)       => apiFetch(`/${id}/vehicle-map`);
export const upsertVehicleMap= (id, data) => apiFetch(`/${id}/vehicle-map`, { method: 'POST', body: JSON.stringify(data) });
export const deleteVehicleMap= (mapId)    => apiFetch(`/vehicle-map/${mapId}`, { method: 'DELETE' });
export const refreshVehicleMap = (id) =>
    apiFetch(`/${id}/vehicle-map/refresh`, { method: 'POST' });

// ── Tracking Events Viewer ────────────────────────────────────────────────────
export const getTrackingEvents = ({ limit = 200, search = '', event_code = '', integration_id = '' } = {}) => {
    const params = new URLSearchParams();
    if (limit)          params.set('limit', limit);
    if (search)         params.set('search', search);
    if (event_code)     params.set('event_code', event_code);
    if (integration_id) params.set('integration_id', integration_id);
    return apiFetch(`/tracking-events?${params.toString()}`);
};

// ── Palletforce Page ──────────────────────────────────────────────────────────
export const getPalletforceManifests = (date) => {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    return apiFetch(`/palletforce/manifests?${params.toString()}`);
};

export const getPalletforceManifestByConsignment = (cn) =>
    apiFetch(`/palletforce/manifests/by-consignment/${encodeURIComponent(cn)}`);

export const getPalletforceTracking = (consignmentNumber) =>
    apiFetch(`/palletforce/tracking/${encodeURIComponent(consignmentNumber)}`);

export const fetchPodImages = (integrationId, trackingNumber) =>
    apiFetch(`/palletforce/${integrationId}/fetch-pod/${encodeURIComponent(trackingNumber)}`, { method: 'POST' });

export const getPodImages = (integrationId, trackingNumber) =>
    apiFetch(`/palletforce/pod-images/${integrationId}/${encodeURIComponent(trackingNumber)}`);

export const getPalletforceTrackingByDate = (date) =>
    apiFetch(`/palletforce/tracking?date=${date}`);
export const syncPalletforceManifest = (integrationId) =>
    apiFetch(`/palletforce/${integrationId}/sync-manifest`, { method: 'POST' });

export const syncPalletforceTracking = (integrationId) =>
    apiFetch(`/palletforce/${integrationId}/sync-tracking`, { method: 'POST' });

export const listCustomers = (integrationId) =>
    apiFetch(`/${integrationId}/list-customers`);

export const listGroups = (integrationId) =>
    apiFetch(`/${integrationId}/list-groups`);

export const getLivePositions = (vehicleId, providers) => {
    const params = new URLSearchParams();
    if (vehicleId) params.set('vehicle_id', vehicleId);
    if (providers && providers.length) params.set('providers', providers.join(','));
    const qs = params.toString();
    return apiFetch(`/live-positions${qs ? '?' + qs : ''}`);
};

export const fetchCustomerPaperwork = (integrationId, consignmentNumber) =>
    apiFetch(`/palletforce/${integrationId}/fetch-paperwork/${encodeURIComponent(consignmentNumber)}`, { method: 'POST' });

export const getCustomerPaperwork = (integrationId, consignmentNumber) =>
    apiFetch(`/palletforce/paperwork/${integrationId}/${encodeURIComponent(consignmentNumber)}`);

// ── PalletTrack / Vigo Page ──────────────────────────────────────────────────
export const getActivePallettrack = () => apiFetch('/pallettrack/active');

export const getPallettrackManifests = (date) => {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    return apiFetch(`/pallettrack/manifests?${params.toString()}`);
};

export const getPallettrackManifestByConsignment = (cn) =>
    apiFetch(`/pallettrack/manifests/by-consignment/${encodeURIComponent(cn)}`);

export const getPallettrackTracking = (consignmentNumber) =>
    apiFetch(`/pallettrack/tracking/${encodeURIComponent(consignmentNumber)}`);

export const getPallettrackTrackingByDate = (date) =>
    apiFetch(`/pallettrack/tracking?date=${encodeURIComponent(date)}`);

export const syncPallettrack = () =>
    apiFetch('/pallettrack/sync', { method: 'POST' });

export const importPallettrackCsv = (file) => {
    const form = new FormData();
    form.append('file', file);
    return apiFetch('/pallettrack/import-csv', { method: 'POST', body: form });
};

export const uploadPallettrackPaperwork = (consignmentNumber, file) => {
    const form = new FormData();
    form.append('file', file);
    return apiFetch(
        `/pallettrack/manifests/${encodeURIComponent(consignmentNumber)}/paperwork`,
        { method: 'POST', body: form },
    );
};

export const getPallettrackPaperwork = (consignmentNumber) =>
    apiFetch(`/pallettrack/manifests/${encodeURIComponent(consignmentNumber)}/paperwork`);

export const getPallettrackDeliveryMedia = (consignmentNumber) =>
    apiFetch(`/pallettrack/manifests/${encodeURIComponent(consignmentNumber)}/delivery-media`);

const downloadPallettrackFile = async (path, filename, errorMessage) => {
    const headers = await authHeaders();
    delete headers['Content-Type'];
    const response = await fetch(`${BASE}${path}`, { headers });
    if (!response.ok) throw new Error(errorMessage);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};

export const downloadPallettrackCsvTemplate = () =>
    downloadPallettrackFile('/pallettrack/csv-template', 'pallettrack-orders-template.csv', 'Template download failed');

export const downloadPallettrackCsvGuide = () =>
    downloadPallettrackFile('/pallettrack/csv-guide', 'pallettrack-csv-field-guide.pdf', 'Field guide download failed');
